/**
 * =============================================================================
 * Hook: useCurrentUser
 * =============================================================================
 *
 * O QUE FAZ:
 *   Fornece o usuario autenticado atual do Supabase Auth para qualquer
 *   componente React. Utiliza um cache singleton em nivel de modulo para
 *   evitar multiplas chamadas a supabase.auth.getUser().
 *
 * QUANDO USAR:
 *   - Em qualquer componente que precise saber quem esta logado (Dashboard,
 *     Sidebar, DbSync, AdminAuthGate, etc.)
 *   - Use getCachedUser() apenas fora de componentes React (callbacks pontuais).
 *
 * COMPORTAMENTOS-CHAVE:
 *   - Cache singleton: uma unica chamada getUser() e compartilhada por todas
 *     as instancias do hook via variaveis de modulo.
 *   - Listener pattern: componentes se inscrevem em atualizacoes e sao
 *     notificados quando o auth muda (login, logout, troca de sessao).
 *   - Cleanup: remove listener no unmount; flag `mounted` previne setState
 *     em componente desmontado.
 *   - onAuthStateChange: assinatura global que atualiza o cache quando o
 *     estado de autenticacao muda.
 *
 * BUGS CONHECIDOS (AUDIT):
 *   Nenhum bug identificado. Implementacao solida com protecao contra
 *   memory leaks e race conditions.
 * =============================================================================
 */

import { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

// ── Cache singleton em nivel de modulo ──
// Compartilhado entre TODAS as instancias do hook para evitar chamadas duplicadas

/**
 * Module-level singleton cache — shared across all hook instances.
 * Eliminates cascading supabase.auth.getUser() calls in DbSync,
 * Dashboard, AdminAuthGate and every other consumer.
 */
let cachedUser: User | null = null; // Usuario cacheado — null se nao autenticado
let cacheResolved = false; // Indica se o bootstrap ja terminou
const listeners = new Set<() => void>(); // Set de funcoes callback para notificar instancias do hook

// Notifica todas as instancias do hook que o cache mudou
function notifyListeners() {
  listeners.forEach(fn => fn());
}

// ── Bootstrap: resolve uma unica vez no carregamento do modulo ──
// Essa promise e executada no import do modulo e preenche o cache inicial
const bootstrap = supabase.auth.getUser().then(({ data: { user } }) => {
  cachedUser = user;
  cacheResolved = true;
  notifyListeners();
});

// ── Assinatura global de mudanca de auth ──
// Atualiza o cache sempre que login/logout/refresh de sessao acontecer
supabase.auth.onAuthStateChange((_event, session) => {
  cachedUser = session?.user ?? null;
  cacheResolved = true;
  notifyListeners();
});

// Interface de retorno simplificada para consumidores que nao precisam do objeto User completo
export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  avatar_url?: string | null;
}

/**
 * Centralized hook for current authenticated user.
 * All instances share a single supabase.auth.getUser() call via module cache.
 */
export function useCurrentUser() {
  // Inicializa com o valor do cache — se ja resolvido, evita loading flash
  const [user, setUser] = useState<User | null>(cachedUser);
  const [loading, setLoading] = useState(!cacheResolved);

  useEffect(() => {
    let mounted = true; // Flag para prevenir setState apos unmount

    // Se o cache ja foi resolvido (bootstrap concluiu), atualiza imediatamente
    if (cacheResolved) {
      setUser(cachedUser);
      setLoading(false);
    }

    // Inscreve este componente para receber atualizacoes futuras de auth
    // A flag `mounted` protege contra setState em componente desmontado
    const update = () => {
      if (!mounted) return;
      setUser(cachedUser);
      setLoading(false);
    };
    listeners.add(update);

    // Garante que o bootstrap foi resolvido (caso o effect rode antes)
    bootstrap.then(update);

    // Cleanup: remove listener e marca como desmontado
    return () => {
      mounted = false;
      listeners.delete(update);
    };
  }, []); // Array vazio: executa apenas na montagem (assinaturas sao globais)

  return {
    user, // Objeto User completo do Supabase
    userId: user?.id ?? null, // Atalho para o ID do usuario
    userEmail: user?.email ?? '', // Atalho para o email
    loading, // true enquanto o bootstrap nao resolver
    isAuthenticated: !!user, // Booleano de conveniencia
  };
}

/**
 * Leitura sincrona para contextos nao-reativos (callbacks pontuais).
 * Sempre prefira o hook useCurrentUser() dentro de componentes React.
 */
export function getCachedUser(): User | null {
  return cachedUser;
}
